import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import Sheet from "./Sheet";
import SplashScreen, { LogoMark, SPLASH_BG, SPLASH_CSS } from "./SplashScreen";
import {
  authenticateUser,
  getEnabledUser,
  hasSavedLogin,
  isNativeApp,
  markAsked,
  setEnabledUser,
  shouldLockAtStart,
  shouldLockOnResume,
  useBiometric,
  wasAsked,
} from "../lib/biometric";

// Depois de quanto tempo fora do app ele pede o Face ID de novo.
const LOCK_AFTER_MS = 2 * 60 * 1000;

// A animacao de abertura aparece uma vez por vez que o app abre (nao a cada tela).
let splashAlreadyShown = false;

// Para ver a animacao no navegador do computador: abra o site com ?splash no final do endereco.
const previewRequested = () => {
  try {
    return new URLSearchParams(window.location.search).has("splash");
  } catch (e) {
    return false;
  }
};

// Mais 7 dias de sessao para quem acabou de destrancar o app.
const renewSession = () => {
  api
    .post("/auth/refresh")
    .then(({ data }) => {
      if (data && data.token) localStorage.setItem("ss_token", data.token);
    })
    .catch(() => {});
};

export default function LaunchGate({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const bio = useBiometric();
  const native = isNativeApp();

  const [showSplash, setShowSplash] = useState(() => !splashAlreadyShown && (native || previewRequested()));
  const [locked, setLocked] = useState(() =>
    shouldLockAtStart({ native, enabledUser: getEnabledUser(), hasToken: hasSavedLogin() })
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [askOpen, setAskOpen] = useState(false);

  const userRef = useRef(user);
  userRef.current = user;
  const busyRef = useRef(false);
  const autoTried = useRef(false);
  const lastTick = useRef(Date.now());
  const hiddenAt = useRef(0);

  const finishSplash = useCallback(() => {
    splashAlreadyShown = true;
    setShowSplash(false);
  }, []);

  // Sem login valido (sessao vencida) ou Face ID de outra conta: nao ha o que trancar.
  useEffect(() => {
    if (!locked || user === null) return;
    if (user === false) {
      setLocked(false);
    } else if (getEnabledUser() && getEnabledUser() !== user.id) {
      setEnabledUser("");
      setLocked(false);
    }
  }, [locked, user]);

  const tryUnlock = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const res = await authenticateUser("Unlock SiteSync");
    busyRef.current = false;
    setBusy(false);
    if (res.ok) {
      setLocked(false);
      renewSession();
    } else if (!res.cancelled) {
      setError(res.message);
    }
  }, []);

  // Pede o Face ID sozinho assim que a abertura termina (uma vez por bloqueio).
  useEffect(() => {
    if (!locked) {
      autoTried.current = false;
      return;
    }
    if (showSplash || !user || autoTried.current) return;
    autoTried.current = true;
    tryUnlock();
  }, [locked, showSplash, user, tryUnlock]);

  // Voltou para o app depois de um tempo fora: tranca de novo.
  useEffect(() => {
    if (!native) return undefined;
    const maybeLock = (awayMs) => {
      const u = userRef.current;
      if (shouldLockOnResume({ native, enabledUser: getEnabledUser(), userId: u && u.id, awayMs, afterMs: LOCK_AFTER_MS })) {
        setLocked(true);
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt.current = Date.now();
      } else {
        const away = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
        hiddenAt.current = 0;
        maybeLock(away);
      }
    };
    // Reforco: o relogio do app para quando ele vai para segundo plano; ao voltar, o salto de tempo denuncia.
    const timer = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTick.current;
      lastTick.current = now;
      if (gap > 20000) maybeLock(gap);
    }, 5000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [native]);

  // Convite para ligar o Face ID: uma vez por pessoa, depois de entrar.
  useEffect(() => {
    if (!native || showSplash || locked || !user || !bio.ready || !bio.supported) return undefined;
    if (bio.enabledUser === user.id || wasAsked(user.id)) return undefined;
    const t = setTimeout(() => setAskOpen(true), 700);
    return () => clearTimeout(t);
  }, [native, showSplash, locked, user, bio.ready, bio.supported, bio.enabledUser]);

  const acceptEnable = async () => {
    const id = user && user.id;
    setAskOpen(false);
    if (!id) return;
    markAsked(id);
    const res = await authenticateUser(`Turn on ${bio.label} for SiteSync`);
    if (res.ok) {
      setEnabledUser(id);
      toast.success(`${bio.label} is on`);
    } else if (!res.cancelled) {
      toast.error(res.message);
    }
  };

  const declineEnable = useCallback(() => {
    if (user && user.id) markAsked(user.id);
    setAskOpen(false);
  }, [user]);

  const signInWithPassword = async () => {
    setLocked(false);
    await logout();
    navigate("/login");
  };

  return (
    <>
      {children}

      {locked && (
        <div
          className="fixed inset-0 flex flex-col items-center justify-center px-6 text-white"
          style={{
            zIndex: 9000,
            background: SPLASH_BG,
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="SiteSync is locked"
          data-testid="lock-screen"
        >
          <style>{SPLASH_CSS}</style>
          <LogoMark size="min(52vw, 26vh)" />
          <div className="mt-6 text-2xl font-bold">SiteSync is locked</div>
          <div className="mt-1 text-center text-sm text-white/70">Use {bio.label} to open the app.</div>
          {error && (
            <div className="mt-4 text-center text-sm text-red-400" data-testid="lock-error">
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={tryUnlock}
            disabled={busy}
            className="mt-8 h-14 w-full max-w-xs rounded-xl bg-primary text-lg font-semibold text-black active:opacity-80 disabled:opacity-60"
            data-testid="unlock-btn"
          >
            {busy ? "Checking..." : `Unlock with ${bio.label}`}
          </button>
          <button
            type="button"
            onClick={signInWithPassword}
            className="mt-3 h-12 w-full max-w-xs rounded-xl text-base font-medium text-white/80 active:bg-white/10"
            data-testid="use-password-btn"
          >
            Sign in with password instead
          </button>
        </div>
      )}

      {askOpen && (
        <Sheet
          title={`Use ${bio.label} to unlock?`}
          subtitle="Open SiteSync faster and keep your account private."
          onClose={declineEnable}
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={declineEnable}
                className="h-12 flex-1 rounded-xl border border-border text-base font-semibold"
                data-testid="bio-not-now"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={acceptEnable}
                className="h-12 flex-1 rounded-xl bg-primary text-base font-semibold text-black"
                data-testid="bio-enable"
              >
                Use {bio.label}
              </button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">
            When you come back to the app after a while, {bio.label} will ask who you are before it opens. You can turn
            this off at any time in the menu.
          </p>
        </Sheet>
      )}

      {showSplash && <SplashScreen ready={user !== null} onDone={finishSplash} />}
    </>
  );
}
